import React, { useState } from 'react'
import { PageHeader } from '@/components/layout'
import { useInstructorCourses } from '@/hooks/useInstructorSplits'
import { useEnrollmentsByCourse } from '@/hooks/useEnrollments'
import { usePracticalScoresBatch } from '@/hooks/useGrading'
import { useAuth } from '@/hooks/useAuth'
import { PracticalScoreRow } from '@/components/grading/PracticalScoreRow'
import type { Enrollment } from '@/types/enrollment'

export const InstructorGradingPage: React.FC = () => {
  const { user } = useAuth()
  const [selectedCourseId, setSelectedCourseId] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const { data: instructorCourses, isLoading: loadingCourses } = useInstructorCourses(user?.id || '')
  const { data: enrollmentsData, isLoading: loadingEnrollments } = useEnrollmentsByCourse(selectedCourseId)

  const courses = Array.isArray(instructorCourses) ? instructorCourses : []

  const enrollments: Enrollment[] = Array.isArray(enrollmentsData)
    ? enrollmentsData
    : (enrollmentsData as any)?.data || []

  const enrollmentIds = enrollments.map((e) => e.id)
  const { data: batchScoresData } = usePracticalScoresBatch(enrollmentIds)
  const batchScores: Record<string, any[]> = batchScoresData || {}

  const filtered = enrollments.filter((e) => {
    if (!searchQuery) return true
    return (
      e.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.userId.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })

  return (
    <div>
      <PageHeader
        title="Grading"
        subtitle="Record practical scores for enrolled students"
      />

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 min-w-0 md:max-w-xs">
          <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1">
            Filter by Course
          </label>
          {loadingCourses ? (
            <div className="h-10 rounded-xl bg-gray-100 dark:bg-surface-card animate-pulse" />
          ) : (
            <select
              value={selectedCourseId}
              onChange={(e) => setSelectedCourseId(e.target.value)}
              className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
            >
              <option value="">Select a course</option>
              {courses.map((c: any) => (
                <option key={c.courseId || c.id} value={c.courseId || c.id}>
                  {c.courseTitle || c.title || c.courseId}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1">
            Search
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 text-[18px]">
              search
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by enrollment or student ID..."
              className="w-full pl-9 pr-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:border-primary/50"
            />
          </div>
        </div>
      </div>

      {/* Enrollments list */}
      <div className="space-y-4">
        {!selectedCourseId ? (
          <div className="text-center py-16 text-gray-500 dark:text-text-muted">
            <div className="size-20 rounded-full bg-white dark:bg-surface-card mx-auto mb-4 flex items-center justify-center">
              <span className="material-symbols-outlined text-[32px]">school</span>
            </div>
            <p className="text-gray-900 dark:text-white font-bold mb-2">Select a course</p>
            <p className="text-sm">Choose a course to view enrolled students.</p>
          </div>
        ) : loadingEnrollments ? (
          <div className="text-center py-12 text-gray-500 dark:text-text-muted">
            Loading enrollments...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500 dark:text-text-muted">
            <div className="size-20 rounded-full bg-white dark:bg-surface-card mx-auto mb-4 flex items-center justify-center">
              <span className="material-symbols-outlined text-[32px]">person_search</span>
            </div>
            <p className="text-gray-900 dark:text-white font-bold mb-2">No enrollments found</p>
            <p className="text-sm">No enrollments match your current filter.</p>
          </div>
        ) : (
          filtered.map((enrollment) => (
            <PracticalScoreRow
              key={enrollment.id}
              enrollment={enrollment}
              trainerId={user?.id || ''}
              scores={batchScores[enrollment.id] || []}
            />
          ))
        )}
      </div>
    </div>
  )
}

export default InstructorGradingPage
